import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Upload } from "lucide-react";
import Seo from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const FORMSPREE_ENDPOINT =
  process.env.REACT_APP_FORMSPREE_ENDPOINT || "https://formspree.io/f/mpqjjqzd";

const CATEGORY_TO_SITE_TYPE = {
  "Salão de Beleza": "servicos",
  Restaurante: "restaurante",
  Fotografia: "portfolio",
  "Oficina Mecânica": "servicos",
};

export default function StyleBriefing() {
  const location = useLocation();
  const navigate = useNavigate();

  const { selectedStyle, selectedCategory, selectedDemo } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const style = params.get("estilo") || "Estilo personalizado";
    const category = params.get("categoria") || "";
    const demo = params.get("demo") || "";

    return {
      selectedStyle: style,
      selectedCategory: category,
      selectedDemo: demo,
    };
  }, [location.search]);

  const [formData, setFormData] = useState({
    businessName: "",
    contactName: "",
    email: "",
    phone: "",
    siteType: CATEGORY_TO_SITE_TYPE[selectedCategory] || "institucional",
    pages: "",
    primaryTone: "#0f766e",
    secondaryTone: "#0f172a",
    toneNotes: "",
    logoStatus: "tenho",
    logoNotes: "",
    references: "",
    deadline: "",
    notes: "",
    consent: false,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const submitWithBrowserPost = (formElement, data) => {
    const nextUrl = `${window.location.origin}/thanks?name=${encodeURIComponent(
      data.contactName || data.businessName || "Cliente"
    )}`;

    formElement.querySelectorAll('[data-briefing-hidden="true"]').forEach((node) => node.remove());

    const fields = {
      style_selected: selectedStyle,
      style_category: selectedCategory,
      style_demo: selectedDemo,
      privacy_consent: "yes",
      _subject: `Novo briefing: ${selectedStyle}`,
      _replyto: data.email,
      _next: nextUrl,
    };

    Object.entries(fields).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = value || "";
      input.setAttribute("data-briefing-hidden", "true");
      formElement.appendChild(input);
    });

    formElement.method = "POST";
    formElement.action = FORMSPREE_ENDPOINT;
    formElement.enctype = "multipart/form-data";
    formElement.submit();
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0] || null;
    setLogoFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (!formData.consent) {
      setErrorMessage("Precisas de aceitar a Política de Privacidade para enviar.");
      return;
    }

    setErrorMessage("");
    setIsSubmitting(true);

    const cleanData = {
      businessName: formData.businessName.trim(),
      contactName: formData.contactName.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      siteType: formData.siteType,
      pages: formData.pages.trim(),
      primaryTone: formData.primaryTone,
      secondaryTone: formData.secondaryTone,
      toneNotes: formData.toneNotes.trim(),
      logoStatus: formData.logoStatus,
      logoNotes: formData.logoNotes.trim(),
      references: formData.references.trim(),
      deadline: formData.deadline.trim(),
      notes: formData.notes.trim(),
    };

    const nameForThanks = cleanData.contactName || cleanData.businessName || "Cliente";

    try {
      const payload = new FormData();
      payload.append("business_name", cleanData.businessName);
      payload.append("contact_name", cleanData.contactName);
      payload.append("email", cleanData.email);
      payload.append("phone", cleanData.phone);
      payload.append("site_type", cleanData.siteType);
      payload.append("pages_needed", cleanData.pages);
      payload.append("style_selected", selectedStyle);
      payload.append("style_category", selectedCategory);
      payload.append("style_demo", selectedDemo);
      payload.append("primary_tone", cleanData.primaryTone);
      payload.append("secondary_tone", cleanData.secondaryTone);
      payload.append("tone_notes", cleanData.toneNotes);
      payload.append("logo_status", cleanData.logoStatus);
      payload.append("logo_notes", cleanData.logoNotes);
      payload.append("references", cleanData.references);
      payload.append("deadline", cleanData.deadline);
      payload.append("notes", cleanData.notes);
      payload.append("privacy_consent", "yes");
      payload.append("_subject", `Novo briefing: ${selectedStyle}`);
      payload.append("_replyto", cleanData.email);

      if (logoFile) {
        payload.append("logo_file", logoFile);
      }

      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: payload,
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        const responseErrors = Array.isArray(errorPayload?.errors)
          ? errorPayload.errors.map((item) => item?.message).filter(Boolean).join(" | ")
          : "";
        const errorText = errorPayload?.error || responseErrors || "";
        const ajaxBlockedByFormspree =
          response.status === 403 && /submit via AJAX|reCAPTCHA|custom key/i.test(errorText);

        if (ajaxBlockedByFormspree) {
          submitWithBrowserPost(event.currentTarget, cleanData);
          return;
        }

        throw new Error(errorText || "Formspree error");
      }

      navigate("/thanks", { state: { name: nameForThanks } });
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
      navigate("/thanks", { state: { name: nameForThanks, submissionFailed: true } });
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-slate-800">
      <Seo
        title="Briefing do Projeto | Web & Studio"
        description="Formulário de briefing para recolher os dados necessários do novo website."
        path="/briefing"
      />

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="mb-8">
          <Link
            to="/#trabalhos"
            className="inline-flex items-center gap-2 text-teal-700 hover:text-teal-800 font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar aos trabalhos
          </Link>
        </div>

        <section className="rounded-3xl border border-slate-200 bg-white p-6 md:p-10 shadow-sm">
          <p className="text-sm font-medium tracking-wide uppercase text-teal-700">Briefing</p>
          <h1 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
            Dados para começarmos o teu site
          </h1>
          <p className="mt-4 text-slate-600 max-w-3xl">
            Preenche os campos com o máximo de detalhe possível. Isto ajuda-nos a preparar proposta,
            estrutura e direção visual com mais rapidez.
          </p>

          <form className="mt-8 space-y-8" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="selectedStyle" className="text-sm font-medium text-slate-700">
                  Estilo escolhido
                </label>
                <Input id="selectedStyle" value={selectedStyle} readOnly className="h-11 bg-slate-50" />
              </div>
              <div className="space-y-2">
                <label htmlFor="selectedCategory" className="text-sm font-medium text-slate-700">
                  Categoria
                </label>
                <Input
                  id="selectedCategory"
                  value={selectedCategory || "Não definido"}
                  readOnly
                  className="h-11 bg-slate-50"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="businessName" className="text-sm font-medium text-slate-700">
                  Nome do negócio
                </label>
                <Input
                  id="businessName"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  className="h-11"
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="contactName" className="text-sm font-medium text-slate-700">
                  Nome do contacto
                </label>
                <Input
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  className="h-11"
                  required
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2 md:col-span-1">
                <label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="h-11"
                  required
                />
              </div>
              <div className="space-y-2 md:col-span-1">
                <label htmlFor="phone" className="text-sm font-medium text-slate-700">
                  Telefone
                </label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="h-11"
                  required
                />
              </div>
              <div className="space-y-2 md:col-span-1">
                <label htmlFor="siteType" className="text-sm font-medium text-slate-700">
                  Tipo de site
                </label>
                <select
                  id="siteType"
                  name="siteType"
                  value={formData.siteType}
                  onChange={handleChange}
                  className="flex h-11 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-700"
                  required
                >
                  <option value="institucional">Institucional</option>
                  <option value="servicos">Serviços</option>
                  <option value="restaurante">Restaurante</option>
                  <option value="portfolio">Portfólio</option>
                  <option value="landing-page">Landing page</option>
                  <option value="ecommerce-catalogo">Catálogo online</option>
                  <option value="outro">Outro</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="pages" className="text-sm font-medium text-slate-700">
                Páginas/áreas que precisas
              </label>
              <Input
                id="pages"
                name="pages"
                value={formData.pages}
                onChange={handleChange}
                placeholder="Ex: Home, Sobre, Serviços, Portfólio, Contacto..."
                className="h-11"
                required
              />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:p-5 space-y-4">
              <h2 className="text-lg font-semibold text-slate-900">Identidade visual</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="primaryTone" className="text-sm font-medium text-slate-700">
                    Tom principal
                  </label>
                  <Input
                    id="primaryTone"
                    type="color"
                    name="primaryTone"
                    value={formData.primaryTone}
                    onChange={handleChange}
                    className="h-11 p-1"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="secondaryTone" className="text-sm font-medium text-slate-700">
                    Tom secundário
                  </label>
                  <Input
                    id="secondaryTone"
                    type="color"
                    name="secondaryTone"
                    value={formData.secondaryTone}
                    onChange={handleChange}
                    className="h-11 p-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="toneNotes" className="text-sm font-medium text-slate-700">
                  Notas sobre cores e estilo visual
                </label>
                <Textarea
                  id="toneNotes"
                  name="toneNotes"
                  value={formData.toneNotes}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Ex: tons sóbrios, minimalista, moderno, sem muito contraste..."
                  required
                />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:p-5 space-y-4">
              <h2 className="text-lg font-semibold text-slate-900">Logo e materiais</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="logoStatus" className="text-sm font-medium text-slate-700">
                    Já tens logotipo?
                  </label>
                  <select
                    id="logoStatus"
                    name="logoStatus"
                    value={formData.logoStatus}
                    onChange={handleChange}
                    className="flex h-11 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-700"
                  >
                    <option value="tenho">Sim, já tenho</option>
                    <option value="nao-tenho">Não tenho ainda</option>
                    <option value="vou-enviar">Tenho e envio depois</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label htmlFor="logoFile" className="text-sm font-medium text-slate-700">
                    Upload do logo (opcional)
                  </label>
                  <label
                    htmlFor="logoFile"
                    className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 bg-white px-3 text-sm text-slate-600 hover:border-slate-400"
                  >
                    <Upload className="h-4 w-4" />
                    {logoFile ? logoFile.name : "Escolher ficheiro"}
                  </label>
                  <input
                    id="logoFile"
                    type="file"
                    name="logo_file"
                    accept=".png,.jpg,.jpeg,.svg,.webp,.pdf"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="logoNotes" className="text-sm font-medium text-slate-700">
                  Notas sobre marca
                </label>
                <Input
                  id="logoNotes"
                  name="logoNotes"
                  value={formData.logoNotes}
                  onChange={handleChange}
                  placeholder="Ex: usar versão horizontal, manter espaço branco, etc."
                  className="h-11"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="references" className="text-sm font-medium text-slate-700">
                  Referências (links)
                </label>
                <Input
                  id="references"
                  name="references"
                  value={formData.references}
                  onChange={handleChange}
                  placeholder="Ex: site1.com, site2.com"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="deadline" className="text-sm font-medium text-slate-700">
                  Prazo desejado
                </label>
                <Input
                  id="deadline"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleChange}
                  placeholder="Ex: até final do mês"
                  className="h-11"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="notes" className="text-sm font-medium text-slate-700">
                Objetivo do site e notas adicionais
              </label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={5}
                placeholder="Conta-nos o objetivo principal, funcionalidades que precisas e qualquer detalhe importante."
                required
              />
            </div>

            <label className="flex items-start gap-3 text-sm text-slate-600">
              <input
                type="checkbox"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-700 focus:ring-teal-700"
              />
              <span>
                Confirmo que li e aceito a{" "}
                <Link to="/privacy" className="font-medium text-teal-700 hover:text-teal-800">
                  Política de Privacidade
                </Link>
                .
              </span>
            </label>

            {errorMessage && (
              <p className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {errorMessage}
              </p>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center rounded-full bg-teal-700 px-7 py-3 font-medium text-white transition-colors hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSubmitting ? "A enviar..." : "Enviar briefing"}
              </button>
              {selectedDemo && (
                <Link
                  to={selectedDemo}
                  className="inline-flex items-center justify-center rounded-full border border-slate-200 px-7 py-3 font-medium text-slate-700 transition-colors hover:bg-slate-50"
                >
                  Ver demo escolhido
                </Link>
              )}
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}




